import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import WidgetShell from '../WidgetShell';
import { InsightModal } from './MyWidgetInsights';
import { usd, tone, shortDate, niceDate, UP, DOWN } from './myWidgetUi';
import { API_ENDPOINTS } from '../../../config';
import { getBearerAuthHeaders } from '../../../utils/authFetchHeaders';
import {
  loadTwAccounts, loadTwHistory, loadTwEvents, loadTwDayEvents, summarizeAccounts, summarizeDays,
  buildAlertFeed, accountIssues, cleanEventDetail,
} from './tradeWatcherData';
import './TradeWatcherWidgets.css';

/**
 * Trade Watcher widgets: the trader's own NinjaTrader accounts from the RiskLo
 * Tally add-on. Live numbers refresh every 30 seconds; the daily history comes
 * from what Trade Watcher recorded each day. Every widget opens a detail view
 * (click a card, a row, a bar or a day), and each detail view links to the
 * Trade Watcher page for that account.
 */

const LIVE_REFRESH_MS = 30 * 1000;

/** Dollars and cents, always two decimals (+$342.50). */
function cents(n) {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v > 0 ? '+' : v < 0 ? '-' : ''}${s}`;
}

function useTw(loader, deps, { refreshMs = 0 } = {}) {
  const [state, setState] = useState({ loading: true });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!refreshMs) return undefined;
    const t = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') setTick((n) => n + 1);
    }, refreshMs);
    return () => clearInterval(t);
  }, [refreshMs]);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: !s.data, error: null }));
    loader()
      .then((data) => { if (live) setState({ loading: false, data }); })
      .catch((err) => {
        if (live) setState({ loading: false, error: err?.message || 'Couldn’t load Trade Watcher', notAvailable: Boolean(err?.notAvailable || err?.requiresPro) });
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { ...state, retry: () => setTick((n) => n + 1) };
}

function agoText(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function forText(iso) {
  return agoText(iso).replace(' ago', '');
}

function whenText(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "Flat · 2m ago" for a live account, "Offline 3h" for one that isn't. */
function accountLine(r) {
  if (r.connected !== 'Yes') {
    const since = r.offlineSince || r.disconnectedSince;
    return since ? `Offline ${forText(since)}` : 'Offline';
  }
  return `${r.position || (r.openPosition ? 'Open position' : 'Flat')} · ${agoText(r.lastSeen)}`;
}

function StatusTag({ row }) {
  if (row.naked === 'Yes') return <span className="tww-tag is-down">Naked</span>;
  if (row.connected !== 'Yes') return <span className="tww-tag is-warn">Offline</span>;
  if (row.clockOk === 'No') return <span className="tww-tag is-warn">Time zone</span>;
  return <span className="tww-tag is-up">OK</span>;
}

const twLink = (ntAccount) => (ntAccount ? `trade-watcher?account=${encodeURIComponent(ntAccount)}` : 'trade-watcher');

/** Not set up yet: point to the Trade Watcher page. */
function SetUp({ onNavigate }) {
  return (
    <div className="tww-state">
      <strong>Trade Watcher isn’t connected</strong>
      <span>Connect a broker or install the RiskLo Tally add-on in NinjaTrader to see your accounts live here.</span>
      {onNavigate ? (
        <button type="button" className="tww-btn" onClick={() => onNavigate('trade-watcher?tab=connections')}>Connect accounts</button>
      ) : null}
    </div>
  );
}

function TwShell({ title, subtitle, state, empty, onNavigate, children, bodyClassName = '' }) {
  let body = children;
  if (state.notAvailable) {
    body = <div className="tww-state"><strong>Trade Watcher</strong><span>Trade Watcher isn’t part of your plan yet.</span></div>;
  } else if (empty) {
    body = <SetUp onNavigate={onNavigate} />;
  }
  return (
    <WidgetShell
      title={title}
      subtitle={subtitle}
      loading={state.loading}
      error={state.notAvailable ? null : state.error}
      onRetry={state.retry}
      bodyClassName={bodyClassName}
    >
      <div className="tww-body">{body}</div>
    </WidgetShell>
  );
}

/** Top right of every detail pop-up: Trade Watcher → Connections. */
function connectMore(onNavigate, onClose) {
  if (!onNavigate) return null;
  return (
    <button type="button" className="tww-connect-btn" onClick={() => { onClose?.(); onNavigate('trade-watcher?tab=connections'); }}>
      + Connect more accounts
    </button>
  );
}

/** Footer of every detail view: straight to the Trade Watcher page. */
function OpenTradeWatcher({ onNavigate, ntAccount, onClose, label }) {
  if (!onNavigate) return null;
  return (
    <div className="tww-modal-foot">
      <button
        type="button"
        className="tww-btn"
        onClick={() => { onClose?.(); onNavigate(twLink(ntAccount)); }}
      >
        {label || (ntAccount ? `Open ${ntAccount} in Trade Watcher →` : 'Open Trade Watcher →')}
      </button>
    </div>
  );
}

/* One account: everything Trade Watcher knows, plus its recent activity ------ */

function AccountDetail({ row, onClose, onNavigate }) {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    let live = true;
    fetch(API_ENDPOINTS.tradeWatcherLogs(row.feedId, row.ntAccount), { headers: getBearerAuthHeaders() })
      .then((r) => r.json())
      .then((d) => { if (live) setLogs(d.success ? (d.rows || []).slice(0, 25) : []); })
      .catch(() => { if (live) setLogs([]); });
    return () => { live = false; };
  }, [row.feedId, row.ntAccount]);
  const issues = accountIssues(row);
  const facts = [
    ['Connection', row.connected === 'Yes' ? 'Connected' : `Offline${row.offlineSince ? ` since ${whenText(row.offlineSince)}` : ''}`],
    ['Position', row.position || (row.openPosition ? 'Open position' : 'Flat')],
    ['Protective stop', row.naked === 'Yes' ? 'Missing (naked)' : 'In place'],
    ['Traded today', row.trading === 'Yes' ? 'Yes' : 'No'],
    ['Auto-close', row.autoClose === true ? 'On' : row.autoClose === false ? 'Off' : '—'],
    ['Time zone', row.clockOk === 'No' ? (row.clockTz || 'Not Eastern') : 'Eastern'],
    ['Last update', `${whenText(row.lastSeen)} (${agoText(row.lastSeen)})`],
  ];
  return (
    <InsightModal title={row.ntAccount} subtitle={`${row.source === 'broker' ? row.brokerName : 'NinjaTrader'} · ${row.client || 'your account'}`} onClose={onClose} headerAction={connectMore(onNavigate, onClose)}>
      <div className="tww-detail-stats">
        <div><span>Running</span><b className={tone(row.runningPnl)}>{cents(row.runningPnl)}</b></div>
        <div><span>Closed today</span><b className={tone(row.dayPnl)}>{cents(row.dayPnl)}</b></div>
        <div><span>Open</span><b className={tone(row.openPnl)}>{cents(row.openPnl)}</b></div>
        {row.accumulatedPnl != null ? <div><span>Accumulated</span><b className={tone(row.accumulatedPnl)}>{cents(row.accumulatedPnl)}</b></div> : null}
      </div>
      {issues.length ? (
        <ul className="tww-issues">
          {issues.map((i) => <li key={i.kind} className={`is-${i.kind}`}>{i.text}</li>)}
        </ul>
      ) : <p className="tww-allclear">All clear: connected, protected and on Eastern time.</p>}
      <dl className="tww-facts">
        {facts.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
      </dl>
      <h4 className="tww-detail-h">Recent activity</h4>
      {logs === null ? <p className="tww-muted">Loading…</p> : !logs.length ? <p className="tww-muted">Nothing recorded for this account yet.</p> : (
        <ul className="tww-log">
          {logs.map((l, i) => (
            <li key={`${l.occurredAt}-${i}`}>
              <time>{whenText(l.occurredAt)}</time>
              <strong>{l.event}</strong>
              <span>{cleanEventDetail(l.detail)}</span>
            </li>
          ))}
        </ul>
      )}
      <OpenTradeWatcher onNavigate={onNavigate} ntAccount={row.ntAccount} onClose={onClose} />
    </InsightModal>
  );
}

/* Every account, by what was clicked: money, attention, connection… --------- */

const OVERVIEW_TABS = [
  { id: 'pnl', label: 'Running P&L' },
  { id: 'attention', label: 'Needs a look' },
  { id: 'connection', label: 'Connection' },
  { id: 'naked', label: 'Naked' },
  { id: 'positions', label: 'Open positions' },
];

function OverviewDetail({ rows, tab: initialTab, onClose, onNavigate }) {
  const [tab, setTab] = useState(initialTab || 'pnl');
  const [account, setAccount] = useState(null);
  const s = summarizeAccounts(rows);
  const byRunning = [...rows].sort((a, b) => (Number(b.runningPnl) || 0) - (Number(a.runningPnl) || 0));
  const attention = rows.filter((r) => accountIssues(r).length);
  const offline = rows.filter((r) => r.connected !== 'Yes');
  const online = rows.filter((r) => r.connected === 'Yes');
  const naked = rows.filter((r) => r.naked === 'Yes');
  const open = rows.filter((r) => r.openPosition === true);
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(Number(r.runningPnl) || 0)));
  const counts = { attention: attention.length, connection: offline.length, naked: naked.length, positions: open.length };

  if (account) return <AccountDetail row={account} onClose={() => setAccount(null)} onNavigate={onNavigate} />;

  const pick = (r) => () => setAccount(r);
  const rowButton = (r, right) => (
    <li key={`${r.feedId}-${r.ntAccount}`}>
      <button type="button" className="tww-list-row" onClick={pick(r)}>
        <i className={`tww-dot ${r.connected === 'Yes' ? 'is-on' : 'is-off'}`} aria-hidden="true" />
        <span className="tww-list-name"><strong>{r.ntAccount}</strong><small>{accountLine(r)}</small></span>
        {right}
        <span className="tww-list-go" aria-hidden="true">›</span>
      </button>
    </li>
  );

  return (
    <InsightModal title="Trade Watcher" subtitle={`${s.accounts} account${s.accounts === 1 ? '' : 's'} · live`} onClose={onClose} wide headerAction={connectMore(onNavigate, onClose)}>
      <div className="mydw-pills tww-tabs" role="tablist" aria-label="View">
        {OVERVIEW_TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
            {t.label}{counts[t.id] ? <b className="tww-tab-count">{counts[t.id]}</b> : null}
          </button>
        ))}
      </div>

      {tab === 'pnl' ? (
        <>
          <div className="tww-detail-stats">
            <div><span>Running</span><b className={tone(s.runningPnl)}>{cents(s.runningPnl)}</b></div>
            <div><span>Closed today</span><b className={tone(s.dayPnl)}>{cents(s.dayPnl)}</b></div>
            <div><span>Open</span><b className={tone(s.openPnl)}>{cents(s.openPnl)}</b></div>
            <div><span>Up / down</span><b>{rows.filter((r) => r.runningPnl > 0).length} / {rows.filter((r) => r.runningPnl < 0).length}</b></div>
          </div>
          <div className="mydw-ins-table-wrap">
            <table className="mydw-ins-table tww-pnl-table">
              <thead><tr><th>Account</th><th className="num">Closed today</th><th className="num">Open</th><th className="num">Running</th><th aria-label="Share" /></tr></thead>
              <tbody>
                {byRunning.map((r) => (
                  <tr key={`${r.feedId}-${r.ntAccount}`} className="is-clickable" onClick={pick(r)}>
                    <td><span className="tww-acct"><i className={`tww-dot ${r.connected === 'Yes' ? 'is-on' : 'is-off'}`} aria-hidden="true" /><span><strong>{r.ntAccount}</strong><small>{accountLine(r)}</small></span></span></td>
                    <td className={`num ${tone(r.dayPnl)}`}>{cents(r.dayPnl)}</td>
                    <td className={`num ${tone(r.openPnl)}`}>{cents(r.openPnl)}</td>
                    <td className={`num ${tone(r.runningPnl)}`}><b>{cents(r.runningPnl)}</b></td>
                    <td className="tww-bar-cell">
                      <span className="tww-bar"><i className={r.runningPnl >= 0 ? 'is-up' : 'is-down'} style={{ width: `${(Math.abs(Number(r.runningPnl) || 0) / maxAbs) * 100}%` }} /></span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td>Total</td><td className={`num ${tone(s.dayPnl)}`}>{cents(s.dayPnl)}</td><td className={`num ${tone(s.openPnl)}`}>{cents(s.openPnl)}</td><td className={`num ${tone(s.runningPnl)}`}>{cents(s.runningPnl)}</td><td /></tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : null}

      {tab === 'attention' ? (attention.length ? (
        <ul className="tww-list">
          {attention.map((r) => rowButton(r, (
            <span className="tww-list-why">{accountIssues(r).map((i) => <em key={i.kind} className={`is-${i.kind}`}>{i.text}</em>)}</span>
          )))}
        </ul>
      ) : <p className="tww-allclear">Nothing needs a look: every account is connected, protected and on Eastern time.</p>) : null}

      {tab === 'connection' ? (
        <>
          {offline.length ? <h4 className="tww-detail-h">Offline ({offline.length})</h4> : <p className="tww-allclear">Every account is connected.</p>}
          {offline.length ? <ul className="tww-list">{offline.map((r) => rowButton(r, <span className="tww-list-right"><StatusTag row={r} /></span>))}</ul> : null}
          {online.length ? <h4 className="tww-detail-h">Connected ({online.length})</h4> : null}
          <ul className="tww-list">{online.map((r) => rowButton(r, <span className="tww-list-right"><StatusTag row={r} /></span>))}</ul>
        </>
      ) : null}

      {tab === 'naked' ? (naked.length ? (
        <ul className="tww-list">
          {naked.map((r) => rowButton(r, <span className="tww-list-right"><span className="tww-list-pos">{r.position || 'Open position'}</span><span className="tww-tag is-down">No stop</span></span>))}
        </ul>
      ) : <p className="tww-allclear">No naked positions: every open position has a protective stop.</p>) : null}

      {tab === 'positions' ? (open.length ? (
        <ul className="tww-list">
          {open.map((r) => rowButton(r, <span className="tww-list-right"><span className="tww-list-pos">{r.position || 'Open position'}</span><b className={tone(r.openPnl)}>{cents(r.openPnl)}</b></span>))}
        </ul>
      ) : <p className="tww-allclear">No open positions right now.</p>) : null}

      <OpenTradeWatcher onNavigate={onNavigate} onClose={onClose} />
    </InsightModal>
  );
}

/* Today: one strip of the numbers that matter right now ----------------------- */

export function TwTodayWidget({ user, onNavigate }) {
  const state = useTw(() => loadTwAccounts(user?.email), [user?.email], { refreshMs: LIVE_REFRESH_MS });
  const rows = useMemo(() => state.data || [], [state.data]);
  const s = useMemo(() => summarizeAccounts(rows), [rows]);
  const [open, setOpen] = useState(null);
  const healthy = s.accounts && !s.naked && !s.disconnected && !s.clockOff;
  const card = (tab, cls, children, label) => (
    <button type="button" className={`tww-stat is-clickable ${cls}`} onClick={() => setOpen(tab)} aria-label={`${label}: details`}>
      {children}
      <span className="tww-stat-more" aria-hidden="true">Details ›</span>
    </button>
  );
  return (
    <TwShell
      title="Trade Watcher today"
      subtitle={s.accounts ? `${s.accounts} account${s.accounts === 1 ? '' : 's'} · live · click any box for details` : 'NinjaTrader and connected brokers'}
      state={state}
      empty={state.data && !s.accounts}
      onNavigate={onNavigate}
    >
      <div className="tww-today">
        {card('pnl', 'is-lead', (
          <>
            <span>Running P&amp;L</span>
            <b className={tone(s.runningPnl)}>{cents(s.runningPnl)}</b>
            <small>Closed {usd(s.dayPnl, { sign: true })} · open {usd(s.openPnl, { sign: true })}</small>
          </>
        ), 'Running P&L')}
        {card('connection', '', (
          <>
            <span>Connected</span>
            <b className={s.disconnected ? 'is-warn' : 'is-up'}>{s.connected}<em>/{s.accounts}</em></b>
            <small>{s.disconnected ? `${s.disconnected} offline` : 'all online'}</small>
          </>
        ), 'Connected')}
        {card('naked', '', (
          <>
            <span>Naked</span>
            <b className={s.naked ? 'is-down' : 'is-up'}>{s.naked}</b>
            <small>{s.naked ? 'no protective stop' : 'all protected'}</small>
          </>
        ), 'Naked')}
        {card('positions', '', (
          <>
            <span>Open positions</span>
            <b>{s.open}</b>
            <small>{s.trading} traded today</small>
          </>
        ), 'Open positions')}
        {card('attention', `tww-health ${healthy ? 'is-ok' : 'is-alert'}`, (
          <>
            <span>Status</span>
            <b>{healthy ? 'All clear' : 'Needs a look'}</b>
            <small>
              {healthy ? 'Connected, protected, Eastern time'
                : [s.naked ? `${s.naked} naked` : null, s.disconnected ? `${s.disconnected} offline` : null, s.clockOff ? `${s.clockOff} time zone` : null].filter(Boolean).join(' · ')}
            </small>
          </>
        ), 'Status')}
      </div>
      {open ? <OverviewDetail rows={rows} tab={open} onClose={() => setOpen(null)} onNavigate={onNavigate} /> : null}
    </TwShell>
  );
}

/* Accounts table --------------------------------------------------------------- */

export function TwAccountsWidget({ user, onNavigate }) {
  const state = useTw(() => loadTwAccounts(user?.email), [user?.email], { refreshMs: LIVE_REFRESH_MS });
  const [picked, setPicked] = useState(null);
  const rows = useMemo(() => [...(state.data || [])].sort((a, b) => (
    // Problems first, then the biggest movers.
    (Number(b.naked === 'Yes') - Number(a.naked === 'Yes'))
    || (Number(a.connected === 'Yes') - Number(b.connected === 'Yes'))
    || (Math.abs(Number(b.runningPnl) || 0) - Math.abs(Number(a.runningPnl) || 0))
  )), [state.data]);
  // Keep the open detail in step with the 30-second refresh.
  const current = picked ? rows.find((r) => r.feedId === picked.feedId && r.ntAccount === picked.ntAccount) || picked : null;
  return (
    <TwShell
      title="NinjaTrader accounts"
      subtitle={rows.length ? 'Live from Trade Watcher · problems first · click an account' : 'NinjaTrader via RiskLo Tally'}
      state={state}
      empty={state.data && !rows.length}
      onNavigate={onNavigate}
    >
      <div className="tww-table-wrap">
        <table className="tww-table">
          <thead>
            <tr><th>Account</th><th className="num">Day</th><th className="num">Open</th><th className="num">Running</th><th>Status</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const off = r.connected !== 'Yes';
              const naked = r.naked === 'Yes';
              return (
                <tr
                  key={`${r.feedId}-${r.ntAccount}`}
                  className={`is-clickable${naked ? ' is-naked' : off ? ' is-off' : ''}`}
                  onClick={() => setPicked(r)}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setPicked(r); }}
                >
                  <td>
                    <span className="tww-acct">
                      <i className={`tww-dot ${off ? 'is-off' : 'is-on'}`} aria-hidden="true" />
                      <span>
                        <strong>{r.ntAccount}</strong>
                        <small>{accountLine(r)}</small>
                      </span>
                    </span>
                  </td>
                  <td className={`num ${tone(r.dayPnl)}`}>{usd(r.dayPnl, { sign: true })}</td>
                  <td className={`num ${tone(r.openPnl)}`}>{usd(r.openPnl, { sign: true })}</td>
                  <td className={`num ${tone(r.runningPnl)}`}><b>{usd(r.runningPnl, { sign: true })}</b></td>
                  <td><StatusTag row={r} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {current ? <AccountDetail row={current} onClose={() => setPicked(null)} onNavigate={onNavigate} /> : null}
    </TwShell>
  );
}

/* One day, every account ----------------------------------------------------- */

const timeText = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
};

function DayDetail({ day, onClose, onNavigate }) {
  const accounts = Object.entries(day.accounts || {}).sort((a, b) => b[1] - a[1]);
  const [events, setEvents] = useState(null);
  const [only, setOnly] = useState(null);
  // The accounts come first; the activity log opens on request (or when an
  // account is picked to see just its activity).
  const [showLog, setShowLog] = useState(false);
  useEffect(() => {
    let live = true;
    loadTwDayEvents(day.date)
      .then((rows) => { if (live) setEvents(rows); })
      .catch(() => { if (live) setEvents([]); });
    return () => { live = false; };
  }, [day.date]);
  const shown = (events || []).filter((e) => !only || e.ntAccount === only);
  return (
    <InsightModal title={niceDate(day.date)} subtitle="Trade Watcher · the day’s P&L and what happened" onClose={onClose} headerAction={connectMore(onNavigate, onClose)}>
      {day.pnl == null ? (
        <p className="tww-muted">No P&amp;L was recorded for this day (Trade Watcher keeps each day’s P&amp;L from when daily history started). Its activity is below.</p>
      ) : (
      <div className="tww-detail-stats">
        <div><span>Day</span><b className={tone(day.pnl)}>{cents(day.pnl)}</b></div>
        <div><span>Accounts</span><b>{accounts.length}</b></div>
        <div><span>Green / red</span><b>{accounts.filter(([, v]) => v > 0).length} / {accounts.filter(([, v]) => v < 0).length}</b></div>
      </div>
      )}
      {accounts.length ? (
      <div className="mydw-ins-table-wrap tww-day-accounts">
        <table className="mydw-ins-table">
          <thead><tr><th>Account</th><th className="num">P&amp;L</th></tr></thead>
          <tbody>
            {accounts.map(([name, v]) => (
              <tr
                key={name}
                className={`is-clickable${only === name ? ' is-selected' : ''}`}
                onClick={() => {
                  setOnly((cur) => (cur === name ? null : name));
                  setShowLog(true);
                }}
                title={only === name ? 'Show every account' : `Only ${name}’s activity`}
              >
                <td>{name}</td><td className={`num ${tone(v)}`}>{cents(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      ) : null}
      <button
        type="button"
        className={`tww-log-toggle${showLog ? ' is-open' : ''}`}
        aria-expanded={showLog}
        onClick={() => setShowLog((v) => !v)}
      >
        <span>
          What happened{only ? ` · ${only}` : ''}
          {events ? <small>{shown.length} event{shown.length === 1 ? '' : 's'}</small> : null}
        </span>
        <span aria-hidden="true">{showLog ? 'Hide ▴' : 'Show ▾'}</span>
      </button>
      {only && showLog ? (
        <button type="button" className="tww-link tww-h-link" onClick={() => setOnly(null)}>Show every account’s activity</button>
      ) : null}
      {!showLog ? null : events === null ? <p className="tww-muted">Loading…</p> : !shown.length ? (
        <p className="tww-muted">No trades or alerts recorded{only ? ` for ${only}` : ''} that day.</p>
      ) : (
        <ul className="tww-log tww-day-log">
          {[...shown].reverse().map((e, i) => (
            <li key={`${e.at}-${i}`}>
              <time>{timeText(e.at)} ET</time>
              <strong>{e.event}{only ? '' : <small> · {e.ntAccount}</small>}</strong>
              <span>{cleanEventDetail(e.detail)}</span>
            </li>
          ))}
        </ul>
      )}
      <OpenTradeWatcher onNavigate={onNavigate} onClose={onClose} />
    </InsightModal>
  );
}

/* Daily P&L chart --------------------------------------------------------------- */

const RANGES = [{ value: 30, label: '1M' }, { value: 90, label: '3M' }, { value: 180, label: '6M' }, { value: 365, label: '1Y' }];

function DailyTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="mydw-tip">
      <span className="mydw-tip-date">{niceDate(row.date)}</span>
      <span className="mydw-tip-row">Day<b className={tone(row.pnl)}>{cents(row.pnl)}</b></span>
      <span className="mydw-tip-row">Total<b className={tone(row.cum)}>{usd(row.cum, { sign: true })}</b></span>
      <span className="mydw-tip-row tww-tip-hint">Click for each account</span>
    </div>
  );
}

function DaysDetail({ days, onClose, onNavigate }) {
  const [day, setDay] = useState(null);
  if (day) return <DayDetail day={day} onClose={() => setDay(null)} onNavigate={onNavigate} />;
  const list = [...days].reverse();
  const s = summarizeDays(days);
  return (
    <InsightModal title="Daily P&L" subtitle={`Trade Watcher · ${days.length} trading day${days.length === 1 ? '' : 's'}`} onClose={onClose} headerAction={connectMore(onNavigate, onClose)}>
      <div className="tww-detail-stats">
        <div><span>Net</span><b className={tone(s.net)}>{cents(s.net)}</b></div>
        <div><span>Average day</span><b className={tone(s.avg)}>{cents(s.avg)}</b></div>
        <div><span>Win days</span><b>{s.winRate == null ? '—' : `${Math.round(s.winRate * 100)}%`}</b></div>
        <div><span>Green / red</span><b>{s.wins} / {s.losses}</b></div>
      </div>
      <div className="mydw-ins-table-wrap tww-days-wrap">
        <table className="mydw-ins-table">
          <thead><tr><th>Day</th><th className="num">P&amp;L</th><th className="num">Accounts</th></tr></thead>
          <tbody>
            {list.map((d) => (
              <tr key={d.date} className="is-clickable" onClick={() => setDay(d)}>
                <td>{niceDate(d.date)}</td>
                <td className={`num ${tone(d.pnl)}`}>{cents(d.pnl)}</td>
                <td className="num">{Object.keys(d.accounts || {}).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OpenTradeWatcher onNavigate={onNavigate} onClose={onClose} />
    </InsightModal>
  );
}

export function TwDailyPnlWidget({ config, onConfigChange, onNavigate, user }) {
  const [days, setDays] = useState(Number(config?.days) || 90);
  useEffect(() => { setDays(Number(config?.days) || 90); }, [config?.days]);
  const accounts = useTw(() => loadTwAccounts(user?.email), [user?.email]);
  const state = useTw(() => loadTwHistory(days), [days]);
  const [openDay, setOpenDay] = useState(null);
  const [openAll, setOpenAll] = useState(false);
  const raw = useMemo(() => state.data?.days || [], [state.data]);
  const data = useMemo(() => {
    let cum = 0;
    return raw.map((d) => {
      cum = Math.round((cum + d.pnl) * 100) / 100;
      return { date: d.date, pnl: d.pnl, cum };
    });
  }, [raw]);
  const s = useMemo(() => summarizeDays(data), [data]);
  const noAccounts = accounts.data && !accounts.data.length;
  const pick = (v) => { setDays(v); if (onConfigChange) onConfigChange({ days: v }); };
  const dayOf = (date) => raw.find((d) => d.date === date);
  return (
    <TwShell
      title="Daily P&L"
      subtitle="Trade Watcher · every NinjaTrader account combined"
      state={state}
      empty={noAccounts}
      onNavigate={onNavigate}
      bodyClassName="dashboard-widget-body-chart"
    >
      <div className="tww-daily">
        <div className="tww-daily-head">
          <div className="tww-daily-stats">
            <span><small>Net</small><b className={tone(s.net)}>{usd(s.net, { sign: true })}</b></span>
            <span><small>Win days</small><b>{s.winRate == null ? '—' : `${Math.round(s.winRate * 100)}%`}</b></span>
            <span><small>Best</small><b className={s.best ? 'is-up' : ''}>{s.best ? usd(s.best.pnl, { sign: true }) : '—'}</b></span>
            <span><small>Worst</small><b className={s.worst ? 'is-down' : ''}>{s.worst ? usd(s.worst.pnl, { sign: true }) : '—'}</b></span>
            <span>
              <small>Streak</small>
              <b className={s.streak > 0 ? 'is-up' : s.streak < 0 ? 'is-down' : ''}>
                {s.streak ? `${Math.abs(s.streak)} ${s.streak > 0 ? 'green' : 'red'}` : '—'}
              </b>
            </span>
          </div>
          <div className="tww-daily-actions">
            {data.length ? <button type="button" className="tww-link" onClick={() => setOpenAll(true)}>Every day ›</button> : null}
            <div className="mydw-pills" role="tablist" aria-label="Period">
              {RANGES.map((r) => (
                <button key={r.value} type="button" role="tab" aria-selected={days === r.value} className={days === r.value ? 'active' : ''} onClick={() => pick(r.value)}>{r.label}</button>
              ))}
            </div>
          </div>
        </div>
        {data.length ? (
          <div className="tww-legend" aria-hidden="true">
            <span><i className="is-bar" />Daily P&amp;L (left)</span>
            <span><i className="is-line" />Running total (right)</span>
            <span>Click a bar for each account</span>
          </div>
        ) : null}
        {data.length < 1 ? (
          <div className="tww-state">
            <strong>No daily history yet</strong>
            <span>Trade Watcher records each day’s P&amp;L from now on. Check back after your next session.</span>
          </div>
        ) : (
          <div className="tww-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 6, right: 8, bottom: 0, left: 0 }}
                onClick={(e) => { const d = e?.activePayload?.[0]?.payload; if (d) setOpenDay(dayOf(d.date)); }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis yAxisId="day" tickFormatter={(v) => usd(v, { compact: true })} tick={{ fontSize: 11 }} width={64} />
                <YAxis yAxisId="cum" orientation="right" tickFormatter={(v) => usd(v, { compact: true })} tick={{ fontSize: 10, fill: 'var(--bt-a5b4fc, #a5b4fc)' }} width={56} axisLine={false} tickLine={false} />
                <Tooltip content={<DailyTip />} cursor={{ fill: 'rgba(148,163,184,0.08)' }} />
                <Bar yAxisId="day" dataKey="pnl" radius={[3, 3, 0, 0]} maxBarSize={data.length < 6 ? 40 : 18}>
                  {data.map((d) => <Cell key={d.date} fill={d.pnl >= 0 ? UP : DOWN} />)}
                </Bar>
                <Line yAxisId="cum" type="monotone" dataKey="cum" name="Running total" stroke="var(--bt-818cf8, #818cf8)" strokeWidth={2} dot={data.length < 3} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {openDay ? <DayDetail day={openDay} onClose={() => setOpenDay(null)} onNavigate={onNavigate} /> : null}
      {openAll ? <DaysDetail days={raw} onClose={() => setOpenAll(false)} onNavigate={onNavigate} /> : null}
    </TwShell>
  );
}

/* P&L calendar --------------------------------------------------------------- */

export function TwCalendarWidget({ user, onNavigate }) {
  const accounts = useTw(() => loadTwAccounts(user?.email), [user?.email]);
  const state = useTw(() => loadTwHistory(365), []);
  const [offset, setOffset] = useState(0);
  const [openDay, setOpenDay] = useState(null);
  const daysByDate = useMemo(() => new Map((state.data?.days || []).map((d) => [d.date, d])), [state.data]);
  const now = new Date();
  const month = new Date(now.getFullYear(), now.getMonth() - offset, 1);
  const total = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < month.getDay(); i += 1) cells.push(null);
  for (let d = 1; d <= total; d += 1) {
    const iso = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const day = daysByDate.get(iso);
    cells.push({ d, iso, pnl: day ? day.pnl : null, day });
  }
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  const inMonth = cells.filter((c) => c && c.pnl != null);
  const monthNet = inMonth.reduce((s, c) => s + c.pnl, 0);
  const maxAbs = Math.max(1, ...inMonth.map((c) => Math.abs(c.pnl)));
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return (
    <TwShell
      title="Trade Watcher calendar"
      subtitle="Daily P&L across your NinjaTrader accounts · click a day"
      state={state}
      empty={accounts.data && !accounts.data.length}
      onNavigate={onNavigate}
    >
      <div className="mydw-cal">
        <div className="mydw-cal-head">
          <button type="button" onClick={() => setOffset((o) => Math.min(11, o + 1))} disabled={offset >= 11} aria-label="Previous month">‹</button>
          <span className="mydw-cal-title">
            {month.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
            <b className={tone(monthNet)}>{usd(monthNet, { sign: true })}</b>
          </span>
          <button type="button" onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset === 0} aria-label="Next month">›</button>
        </div>
        <div className="mydw-cal-grid" style={{ gridTemplateRows: `auto repeat(${weeks.length}, minmax(0, 1fr))` }}>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((w, i) => <span key={`h${i}`} className="mydw-cal-dow">{w}</span>)}
          <span className="mydw-cal-dow">Week</span>
          {weeks.map((week, wi) => {
            const withData = week.filter((c) => c && c.pnl != null);
            const wk = withData.reduce((s, c) => s + c.pnl, 0);
            return (
              <React.Fragment key={`w${wi}`}>
                {week.map((c, i) => (c ? (
                  <button
                    type="button"
                    key={`d${wi}-${i}`}
                    className={`mydw-cal-day ${c.pnl != null ? tone(c.pnl) : ''}${c.iso === todayIso ? ' is-today' : ''}${c.iso > todayIso ? ' is-future' : ''}`}
                    style={c.pnl != null ? { '--mydw-int': 0.15 + 0.6 * (Math.abs(c.pnl) / maxAbs) } : undefined}
                    onClick={() => setOpenDay(c.day || { date: c.iso, pnl: null, accounts: {} })}
                    disabled={c.iso > todayIso}
                    aria-label={c.pnl != null ? `${niceDate(c.iso)}: ${cents(c.pnl)}` : niceDate(c.iso)}
                  >
                    <span className="mydw-cal-day-top"><i>{c.d}</i></span>
                    {c.pnl != null ? <em>{usd(c.pnl, { sign: true, compact: true })}</em> : null}
                  </button>
                ) : <span key={`e${wi}-${i}`} className="mydw-cal-empty" />))}
                <span className={`mydw-cal-week ${withData.length ? tone(wk) : ''}`}>{withData.length ? usd(wk, { sign: true, compact: true }) : ''}</span>
              </React.Fragment>
            );
          })}
        </div>
        <div className="mydw-cal-foot">
          <span><i className="is-up" />{inMonth.filter((c) => c.pnl > 0).length} green</span>
          <span><i className="is-down" />{inMonth.filter((c) => c.pnl < 0).length} red</span>
        </div>
      </div>
      {openDay ? <DayDetail day={openDay} onClose={() => setOpenDay(null)} onNavigate={onNavigate} /> : null}
    </TwShell>
  );
}

/* Alerts --------------------------------------------------------------- */

const ALERT_ICON = {
  naked: { cls: 'is-down', icon: '!' },
  disconnect: { cls: 'is-warn', icon: '⏻' },
  resolved: { cls: 'is-muted', icon: '⏻' },
  reconnect: { cls: 'is-up', icon: '↻' },
  autoClose: { cls: 'is-good', icon: '✓' },
  clock: { cls: 'is-warn', icon: '◷' },
  settings: { cls: 'is-info', icon: '⚙' },
  other: { cls: 'is-info', icon: '•' },
};

function AlertList({ items, onPick, max }) {
  const shown = max ? items.slice(0, max) : items;
  return (
    <ul className="tww-alerts">
      {shown.map((e, i) => {
        const k = ALERT_ICON[e.kind] || ALERT_ICON.other;
        return (
          <li key={`${e.at}-${e.ntAccount}-${i}`}>
            <button type="button" className="tww-alert-row" onClick={() => onPick(e.ntAccount)}>
              <span className={`tww-alert-icon ${k.cls}`} aria-hidden="true">{k.icon}</span>
              <span className="tww-alert-text">
                <strong>{e.title}{e.kind === 'resolved' ? <em className="tww-resolved">resolved</em> : null}</strong>
                <small>{e.ntAccount}{e.detail ? ` · ${e.detail}` : ''}</small>
              </span>
              <time dateTime={e.at} title={whenText(e.at)}>{agoText(e.at)}</time>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function AlertsDetail({ events, rows, onClose, onNavigate, onPick }) {
  const [important, setImportant] = useState(true);
  const items = buildAlertFeed(events, rows, { important });
  return (
    <InsightModal title="Alerts" subtitle="Trade Watcher · your accounts" onClose={onClose} wide headerAction={connectMore(onNavigate, onClose)}>
      <div className="mydw-pills tww-tabs" role="tablist" aria-label="Show">
        <button type="button" role="tab" aria-selected={important} className={important ? 'active' : ''} onClick={() => setImportant(true)}>Important</button>
        <button type="button" role="tab" aria-selected={!important} className={!important ? 'active' : ''} onClick={() => setImportant(false)}>Everything</button>
      </div>
      <p className="tww-muted">
        {important
          ? 'Naked positions, auto-closes, accounts still offline, outages over 15 minutes and setting changes. Short drops that came straight back are left out.'
          : 'Every event, with each disconnect paired with the reconnect that followed it.'}
      </p>
      {items.length ? <AlertList items={items} onPick={onPick} /> : <p className="tww-allclear">Nothing to show.</p>}
      <OpenTradeWatcher onNavigate={onNavigate} onClose={onClose} />
    </InsightModal>
  );
}

export function TwAlertsWidget({ user, onNavigate }) {
  const accounts = useTw(() => loadTwAccounts(user?.email), [user?.email], { refreshMs: LIVE_REFRESH_MS });
  const state = useTw(() => loadTwEvents(100), [], { refreshMs: 60 * 1000 });
  const rows = useMemo(() => accounts.data || [], [accounts.data]);
  const [openAll, setOpenAll] = useState(false);
  const [picked, setPicked] = useState(null);
  // Right now, from the live board: what's wrong at this moment.
  const now = useMemo(() => rows.flatMap((r) => accountIssues(r).map((i) => ({ ...i, row: r }))), [rows]);
  const feed = useMemo(() => buildAlertFeed(state.data || [], rows, { important: true }), [state.data, rows]);
  const pickAccount = (ntAccount) => {
    const row = rows.find((r) => r.ntAccount === ntAccount);
    if (row) setPicked(row);
    else if (onNavigate) onNavigate(twLink(ntAccount));
  };
  const current = picked ? rows.find((r) => r.feedId === picked.feedId && r.ntAccount === picked.ntAccount) || picked : null;
  return (
    <TwShell
      title="Alerts"
      subtitle="What needs a look now, then what happened"
      state={state}
      empty={accounts.data && !rows.length}
      onNavigate={onNavigate}
    >
      <div className="tww-alerts-wrap">
        <div className="tww-now">
          <span className="tww-now-h">Right now</span>
          {now.length ? (
            <ul className="tww-now-list">
              {now.map((n) => (
                <li key={`${n.row.ntAccount}-${n.kind}`}>
                  <button type="button" className={`tww-now-item is-${n.kind}`} onClick={() => setPicked(n.row)}>
                    <strong>{n.row.ntAccount}</strong>
                    <span>{n.kind === 'naked' ? 'Naked, no stop' : n.kind === 'offline' ? `Offline${n.row.offlineSince ? ` ${forText(n.row.offlineSince)}` : ''}` : 'Time zone not Eastern'}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : <span className="tww-now-ok">All clear</span>}
        </div>
        <div className="tww-recent-head">
          <span className="tww-now-h">Recent</span>
          <button type="button" className="tww-link" onClick={() => setOpenAll(true)}>All alerts ›</button>
        </div>
        {feed.length ? <AlertList items={feed} onPick={pickAccount} max={20} /> : (
          <div className="tww-state"><strong>Nothing important lately</strong><span>Short disconnects that came straight back are in All alerts.</span></div>
        )}
      </div>
      {openAll ? <AlertsDetail events={state.data || []} rows={rows} onClose={() => setOpenAll(false)} onNavigate={onNavigate} onPick={(a) => { setOpenAll(false); pickAccount(a); }} /> : null}
      {current ? <AccountDetail row={current} onClose={() => setPicked(null)} onNavigate={onNavigate} /> : null}
    </TwShell>
  );
}
